import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getLoginReturnPath } from '../../utils/returnUrl';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import LoginErrorModal from '../../components/LoginErrorModal';
import apiService from '../../services/api';
import './password-dialog-overrides.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Password change modal state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = getLoginReturnPath(searchParams);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowErrorModal(false);
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check if password change is required
        if (result.requiresPasswordChange) {
          setShowPasswordChangeModal(true);
        } else {
          navigate(returnPath, { replace: true });
        }
      } else {
        setError(result.message);
        setShowErrorModal(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordChangeError('');
    
    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setPasswordChangeError('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordChangeError('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordChangeError('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await apiService.forceChangePassword(newPassword);
      if (response.success) {
        setShowPasswordChangeModal(false);
        window.location.assign(returnPath || '/');
      } else {
        setPasswordChangeError(response.message || 'Failed to change password');
      }
    } catch (err) {
      setPasswordChangeError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      className="login-page"
      sx={{
        minHeight: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        padding: isMobile ? '16px' : '0'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, transparent 70%)',
          animation: 'float 30s ease-in-out infinite',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-50%',
          right: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(0, 188, 212, 0.05) 0%, transparent 70%)',
          animation: 'float 35s ease-in-out infinite reverse',
          zIndex: 0
        }}
      />
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            minHeight: isMobile ? 'auto' : '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: isMobile ? 2 : 4
          }}
        >
          <Paper
            elevation={8}
            className="glass"
            sx={{
              p: isMobile ? 3 : 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: isMobile ? '100%' : 360,
              minHeight: isMobile ? 'auto' : 500,
              borderRadius: isMobile ? 2 : 2,
              background: 'var(--color-bg-alt)',
              backdropFilter: 'blur(20px)',
              border: '1px solid var(--color-border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)',
            }}
          >
            {/* Logo/Title */}
            <Box
              sx={{
                mb: isMobile ? 3 : 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                component="h1"
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  textAlign: 'center',
                  color: 'var(--color-text-secondary)',
                  fontSize: isMobile ? '14px' : '16px'
  }}
              >
                Sign in to your account to continue
              </Typography>
            </Box>


            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" style={{ color: 'var(--color-text-secondary)' }}/>
                    </InputAdornment>
                  ),
                  style: {    
                    fontSize: isMobile ? '16px' : '14px',
                    minHeight: isMobile ? '48px' : '52px'
                  }
                }}
                sx={{ 
                  mb: 3,
                  '& .MuiInputBase-root': {
                    background: 'var(--color-bg-input)',
                    color: 'var(--color-text)',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'var(--color-text-secondary)',
                    fontSize: isMobile ? '16px' : '14px'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--color-text-secondary)'
                  },
                  '& .MuiInputLabel-root.Mui-error': {
                    color: 'var(--color-text-secondary)'
                  },
                  '& .MuiInputBase-root.Mui-focused': {
                    background: 'var(--color-bg-input-focused)'
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action"  style={{ color: 'var(--color-text-secondary)' }}/>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: 'var(--color-text-secondary)',
                          minWidth: isMobile ? '44px' : 'auto',
                          minHeight: isMobile ? '44px' : 'auto'
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: {    
                    fontSize: isMobile ? '16px' : '14px',
                    minHeight: isMobile ? '48px' : '52px'
                  }
                }}
                sx={{ 
                  mb: 1,
                  '& .MuiInputBase-root': {
                    background: 'var(--color-bg-input)',
                    color: 'var(--color-text)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'var(--color-text-secondary)',
                    fontSize: isMobile ? '16px' : '14px'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--color-text-secondary)'
                  },
                  '& .MuiInputLabel-root.Mui-error': {
                    color: 'var(--color-text-secondary)'
                  },
                  
                  '& .MuiInputBase-root.Mui-focused': {
                    background: 'var(--color-bg-input-focused)'
                  },
                }}
              />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  component={Link}
                  to="/forgot-password"
                  variant="text"
                  sx={{
                    textTransform: 'none',
                    color: 'var(--color-accent)',
                    fontSize: isMobile ? '14px' : '13px',
                    minHeight: isMobile ? '44px' : 'auto',
                    px: 0,
                    '&:hover': {
                      background: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot password?
                </Button>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: isMobile ? 1.5 : 1.5,
                  fontSize: isMobile ? '24px' : '28px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
                  border: 'none',
                  borderRadius: '8px',
                  textTransform: 'none',
                  minHeight: isMobile ? '48px' : '52px',
                  '&:hover': {
                    background: 'var(--color-accent)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: 'var(--color-bg-disabled)',
                    color: 'var(--color-text-disabled)'
                  },
                }}
              >
                {loading ? (
                  <CircularProgress 
                    size={isMobile ? 24 : 20} 
                    sx={{ color: 'var(--color-text)' }} 
                  />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
      
      {/* Error Modal */}
      <LoginErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        error={error}
        isMobile={isMobile}
      />

      {/* Forced Password Change Modal */}
      <Dialog
        open={showPasswordChangeModal}
        disableEscapeKeyDown
        className="password-change-dialog"
        PaperProps={{
          sx: {
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-border)',
            width: '520px',
            maxWidth: '520px',
            m: 2,
            overflow: 'visible !important',
            '&.password-change-dialog': {
              overflow: 'visible !important',
            },
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-text)', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Password Change Required
          </Typography>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            px: 3, 
            pb: 2, 
            pt: 3, 
            overflow: 'visible !important',
            '&.MuiDialogContent-root': {
              overflow: 'visible !important',
              paddingTop: '32px !important',
            },
            '& > *': {
              overflow: 'visible !important',
            },
            '& .MuiTextField-root': {
              overflow: 'visible !important',
              overflowX: 'visible !important',
            },
            '& .MuiFormControl-root': {
              overflow: 'visible !important',
              overflowX: 'visible !important',
            },
          }}
        >
          <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '1px solid var(--color-warning)' }}>
            You must change your password before continuing. Your current password is the default password.
          </Alert>
          
          {passwordChangeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordChangeError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              InputLabelProps={{
                style: { 
                  color: 'var(--color-text-secondary)', 
                  fontSize: '16px',
                  overflow: 'visible !important',
                  overflowX: 'visible !important',
                  whiteSpace: 'nowrap',
                  width: 'auto !important',
                  maxWidth: 'none !important'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock style={{ color: 'var(--color-text-secondary)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      sx={{ color: 'var(--color-text-secondary)' }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                overflow: 'visible !important',
                width: '100%',
                '& *': {
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputBase-root': {
                  background: 'var(--color-bg-input)',
                  color: 'var(--color-text)',
                  height: '64px !important',
                  minHeight: '64px !important',
                  overflow: 'hidden !important',
                  position: 'relative',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiFormControl-root': {
                  overflow: 'visible !important',
                  width: '100%',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiFormLabel-root': {
                  overflow: 'visible !important',
                  width: 'auto !important',
                  maxWidth: 'none !important',
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--color-text-secondary)',
                  fontSize: '16px',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  transform: 'translate(14px, 22px) scale(1)',
                  pointerEvents: 'none',
                  overflow: 'visible !important',
                  whiteSpace: 'nowrap',
                  maxWidth: 'none !important',
                  width: 'auto !important',
                  zIndex: 10,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'var(--color-text-secondary)',
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75) !important',
                },
                '&.Mui-focused .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '&:has(.MuiInputBase-input:not(:placeholder-shown)) .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-root': {
                  overflow: 'hidden !important',
                  height: '64px !important',
                  minHeight: '64px !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                  '& fieldset': {
                    borderColor: 'var(--color-border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--color-accent)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-accent)',
                  },
                  '& input': {
                    overflow: 'hidden !important',
                    textOverflow: 'ellipsis',
                    height: '100% !important',
                    padding: '0 !important',
                    '&::-webkit-scrollbar': {
                      display: 'none !important',
                      width: '0 !important',
                      height: '0 !important',
                    },
                    scrollbarWidth: 'none !important',
                    msOverflowStyle: 'none !important',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '20px 14px !important',
                  fontSize: '16px',
                  height: '100% !important',
                  lineHeight: '1.4375em !important',
                  overflow: 'hidden !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputBase-input': {
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputAdornment-root': {
                  height: '100%',
                  maxHeight: 'none',
                },
              }}
            />
            
            <TextField
              fullWidth
              variant="outlined"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputLabelProps={{
                style: { 
                  color: 'var(--color-text-secondary)', 
                  fontSize: '16px',
                  overflow: 'visible !important',
                  overflowX: 'visible !important',
                  whiteSpace: 'nowrap',
                  width: 'auto !important',
                  maxWidth: 'none !important'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock style={{ color: 'var(--color-text-secondary)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: 'var(--color-text-secondary)' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                overflow: 'visible !important',
                width: '100%',
                '& *': {
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputBase-root': {
                  background: 'var(--color-bg-input)',
                  color: 'var(--color-text)',
                  height: '64px !important',
                  minHeight: '64px !important',
                  overflow: 'hidden !important',
                  position: 'relative',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiFormControl-root': {
                  overflow: 'visible !important',
                  width: '100%',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiFormLabel-root': {
                  overflow: 'visible !important',
                  width: 'auto !important',
                  maxWidth: 'none !important',
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--color-text-secondary)',
                  fontSize: '16px',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  transform: 'translate(14px, 22px) scale(1)',
                  pointerEvents: 'none',
                  overflow: 'visible !important',
                  whiteSpace: 'nowrap',
                  maxWidth: 'none !important',
                  width: 'auto !important',
                  zIndex: 10,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'var(--color-text-secondary)',
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75) !important',
                },
                '&.Mui-focused .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '&:has(.MuiInputBase-input:not(:placeholder-shown)) .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-root': {
                  overflow: 'hidden !important',
                  height: '64px !important',
                  minHeight: '64px !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                  '& fieldset': {
                    borderColor: 'var(--color-border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--color-accent)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-accent)',
                  },
                  '& input': {
                    overflow: 'hidden !important',
                    textOverflow: 'ellipsis',
                    height: '100% !important',
                    padding: '0 !important',
                    '&::-webkit-scrollbar': {
                      display: 'none !important',
                      width: '0 !important',
                      height: '0 !important',
                    },
                    scrollbarWidth: 'none !important',
                    msOverflowStyle: 'none !important',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '20px 14px !important',
                  fontSize: '16px',
                  height: '100% !important',
                  lineHeight: '1.4375em !important',
                  overflow: 'hidden !important',
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputBase-input': {
                  '&::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0 !important',
                    height: '0 !important',
                  },
                  scrollbarWidth: 'none !important',
                  msOverflowStyle: 'none !important',
                },
                '& .MuiInputAdornment-root': {
                  height: '100%',
                  maxHeight: 'none',
                },
              }}
            />
            
            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontSize: '12px', mt: -1 }}>
              Password must be at least 8 characters long
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--color-border)' }}>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={changingPassword || !newPassword || !confirmPassword}
            sx={{
              background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
              '&:hover': {
                background: 'var(--color-accent)',
              },
              '&:disabled': {
                background: 'var(--color-bg-disabled)',
              },
            }}
          >
            {changingPassword ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'var(--color-text)' }} />
                Changing...
              </>
            ) : (
              'Change Password'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage; 




